import { useEffect, useRef } from 'react';
import type { PublicStore } from '@/features/menu/types';
import { haversineDistanceKm } from '@/features/location/distance';
import { useSharedCustomerLocation } from '@/features/location/useCustomerLocation';
import {
  browseStoreFromPublic,
  type BrowseStore,
  useBrowseStore,
} from '@/state/browseStoreStore';
import { useCustomerSessionStore } from '@/state/customerSessionStore';
import { usePublicStores } from './discoveryApi';

let pageSessionAttemptCompleted = false;

export function nearestAcceptingStore(
  stores: PublicStore[],
  customerLocation?: { latitude: number; longitude: number }
): PublicStore | undefined {
  if (!customerLocation) return undefined;
  return stores
    .map((store, index) => ({ store, index, distance: haversineDistanceKm(customerLocation, store) }))
    .filter((item): item is { store: PublicStore; index: number; distance: number } =>
      Boolean(item.store.acceptingOrders) && item.distance !== undefined
    )
    .sort((left, right) => left.distance - right.distance || left.index - right.index)[0]?.store;
}

export function deterministicFallbackStore(stores: PublicStore[]): PublicStore | undefined {
  return stores.find((store) => store.acceptingOrders) ?? stores[0];
}

export function selectAutomaticBrowseStore(
  stores: PublicStore[],
  options: {
    orderingStoreCode?: string;
    persistedBrowseStore?: BrowseStore | null;
    customerLocation?: { latitude: number; longitude: number };
  }
): { store: PublicStore; source: 'qr' | 'manual' | 'nearest' | 'fallback' } | undefined {
  const byCode = (code?: string) =>
    stores.find((store) => store.storeCode?.trim().toUpperCase() === code?.trim().toUpperCase());
  const qrStore = byCode(options.orderingStoreCode);
  if (qrStore) return { store: qrStore, source: 'qr' };

  const manualStore =
    options.persistedBrowseStore?.selectionSource === 'manual'
      ? byCode(options.persistedBrowseStore.storeCode)
      : undefined;
  if (manualStore) return { store: manualStore, source: 'manual' };

  const nearest = nearestAcceptingStore(stores, options.customerLocation);
  if (nearest) return { store: nearest, source: 'nearest' };
  const fallback = deterministicFallbackStore(stores);
  return fallback ? { store: fallback, source: 'fallback' } : undefined;
}

/** Coordinates are used only in memory to choose a browse preference. */
export function useAutomaticBrowseStore() {
  const stores = usePublicStores();
  const location = useSharedCustomerLocation();
  const browseStore = useBrowseStore((state) => state.store);
  const selectStore = useBrowseStore((state) => state.selectStore);
  const orderingStore = useCustomerSessionStore((state) => state.store);
  const locationRequested = useRef(false);

  useEffect(() => {
    const availableStores = stores.data;
    if (!availableStores || stores.isPending || stores.isError) return;

    const orderingStoreCode = orderingStore?.storeCode?.trim().toUpperCase();
    if (orderingStoreCode) {
      const verifiedQrStore = selectAutomaticBrowseStore(availableStores, { orderingStoreCode });
      // A QR Store is authoritative only after it is visible in the public Store projection.
      if (!verifiedQrStore || verifiedQrStore.source !== 'qr') return;
      if (
        browseStore?.storeCode !== verifiedQrStore.store.storeCode ||
        browseStore?.selectionSource !== 'qr'
      )
        selectStore(browseStoreFromPublic(verifiedQrStore.store), 'qr');
      pageSessionAttemptCompleted = true;
      return;
    }

    const validManual = selectAutomaticBrowseStore(availableStores, { persistedBrowseStore: browseStore });
    if (validManual?.source === 'manual' || (browseStore && pageSessionAttemptCompleted)) return;

    if (location.status === 'idle' && !locationRequested.current) {
      locationRequested.current = true;
      location.requestLocation();
      return;
    }
    if (location.status === 'requesting') return;

    const selected = selectAutomaticBrowseStore(availableStores, {
      customerLocation: location.status === 'granted' ? location.location : undefined,
    });
    if (!selected) {
      pageSessionAttemptCompleted = true;
      return;
    }
    selectStore(browseStoreFromPublic(selected.store), selected.source);
    pageSessionAttemptCompleted = true;
  }, [browseStore, location, orderingStore?.storeCode, selectStore, stores.data, stores.isError, stores.isPending]);

  return { stores, location };
}
